import moment from 'moment';
import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import {
    cleanDate,
    cleanHour,
    DEFAULT_SLOT_DURATION_IN_MINUTES,
    isSessionSingleEvent,
} from '../../utils/edReviewUtils';
import {
    getIntervalRrule,
} from './rruleUtils';
import {
    INITIAL_SESSION_STATE,
    SLOT_MANIPULATION_OPTIONS,
} from '../../utils/constants/appointmentSessionConstants';

const sessionIsLocked = (session) => session.status === 'LOCKED';

////////////////////////////////////////////////////////
///// SESS INTERVAL CHECK  /////
//////////////////////////////////////////////////////
const parseDefaultOption = (option) => {
    if (!option) return;

    const amount = option.at(0);
    const optionType = option.slice(1);

    return [amount, optionType];
};

export const getMinMaxDate = (
    date,
    endDate = true,
    {
        intervalType,
        customInterval,
        customIntervalNumber,
        customIntervalType,
        daysOfWeek,
    },
) => {
    const startClone = moment(date);
    let extendedTime = moment(date);

    let parsedInterval;
    let firstOrLastSessionOnIsoDayOfWeek;

    if (!customInterval) {
        parsedInterval = parseDefaultOption(intervalType);
    } else {
        parsedInterval = [customIntervalNumber, customIntervalType];
    }

    if (!_.isEmpty(daysOfWeek)) {
        firstOrLastSessionOnIsoDayOfWeek = endDate
            ? _.last(daysOfWeek)
            : _.first(daysOfWeek);
    }

    if (parsedInterval) {
        extendedTime = endDate
            ? startClone.add(parsedInterval[0], parsedInterval[1])
            : startClone.subtract(parsedInterval[0], parsedInterval[1]);
    }

    if (firstOrLastSessionOnIsoDayOfWeek) {
        if (endDate) {
            extendedTime.add(firstOrLastSessionOnIsoDayOfWeek - 1, 'days');
        } else {
            extendedTime.subtract(firstOrLastSessionOnIsoDayOfWeek - 1, 'days');
        }
    }

    return moment(extendedTime);
};

////////////////////////////////////////////////
/////   SESSION SLOTS  /////
///////////////////////////////////////////////

export const getSessionSlotDuration = (sessionObject) =>
    sessionObject?.slotDuration ||
    sessionObject?.slotDurationInMinutes ||
    DEFAULT_SLOT_DURATION_IN_MINUTES;

////////////////////////////////////////////////
/////   DATA PREPARING  /////
///////////////////////////////////////////////

export const prepareCurrentSessionForEditState = (session) => {
    let preparedObject = _.cloneDeep(INITIAL_SESSION_STATE);
    const isSingleEvent = isSessionSingleEvent(session);
    const isLocked = sessionIsLocked(session);
    const start = moment(session?.from || session?.startDate);
    const end = moment(session?.to || session?.endDate);
    const sessionName = session?.sessionName;

    preparedObject = {
        ...preparedObject,
        startTime: moment(cleanDate(start)),
        startDate: moment(cleanHour(start)),
        endTime: moment(cleanDate(end)),
        endDate: moment(cleanHour(end)),
        newEndDate: moment(cleanHour(end)),
        locked: isLocked,
        active: session?.status === 'ACTIVE' || true,
        sessionId: session?.sessionId,
    };

    if (sessionName) {
        preparedObject.sessionName = sessionName;
    }

    if (session.slotDurationInMinutes) {
        preparedObject.fixedSlotDuration = session.slotDurationInMinutes !== 30;
        preparedObject.slotDurationInMinutes = session.slotDurationInMinutes;
    }

    preparedObject.isBars = session?.isBars;
    preparedObject.clinicsId = session?.clinicsId;
    preparedObject.assignedSpecialty = session?.assignedSpecialty;
    preparedObject.healthCareService = session?.healthCareService;
    preparedObject.practitioner = session?.practitioner;
    preparedObject.practitionerRole = session?.practitionerRole;
    preparedObject.wardId = session?.wardId;

    //  Custom times ("HH:mm") are already UTC-formatted in db.
    if (!_.isEmpty(session?.customSessionTime)) {
        let customSessionTimes = {};

        Object.keys(session?.customSessionTime).forEach((key, index) => {
            customSessionTimes[key] = {
                startTime: moment(
                    session?.customSessionTime[key]?.startTime,
                    'HH:mm',
                ).toISOString(),
                endTime: moment(
                    session?.customSessionTime[key]?.endTime,
                    'HH:mm',
                ).toISOString(),
            };
        });

        preparedObject.customSessionTime = customSessionTimes;
    }

    const additionalFieldsToCheck = [
        'customInterval',
        'customIntervalNumber',
        'customIntervalType',
        'daysOfWeek',
        'intervalModeOn',
        'intervalType',
        'lockedReason',
        'omitDates',
    ];

    if (isSingleEvent) {
        preparedObject.singleEvent = true;
    } else {
        for (let additionalField of additionalFieldsToCheck) {
            if (_.isBoolean(session[additionalField]) || !!session[additionalField]) {
                preparedObject[additionalField] = session[additionalField];
            }
        }
    }

    return preparedObject;
};

// Create new sessions for each day in sessionState?.customSessionTime;
// New session creation draft
const getSessionStatus = (sessObj) => {
    if (sessObj.hasOwnProperty('status')) {
        return sessObj.status;
    } else {
        if (sessObj.active) return 'active';
        if (sessObj.locked) return 'locked';

        return '';
    }
};


export const createBarsSessionObject = (sessionState) => {
    const startDate = moment(sessionState.startDate)
        .set({
            hours: sessionState.startTime.hours(),
            minutes: sessionState.startTime.minutes(),
        })
        .utc();

    const endDate = moment(sessionState.endDate)
        .set({
            hours: sessionState.endTime.hours(),
            minutes: sessionState.endTime.minutes(),
        })
        .utc();

    return {
        sessionId: sessionState?.sessionId || sessionState?.id || uuidv4(),
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        status: getSessionStatus(sessionState),
        sessionName: sessionState.sessionName || '',
        slotStartTime: startDate.format('HH:mm'),
        slotDurationInMinutes: getSessionSlotDuration(sessionState),
        healthCareService: sessionState.healthCareService,
        assignedSpecialty: sessionState.assignedSpecialty,
        rrule: getIntervalRrule({
            ...sessionState,
            endDate: endDate.toISOString(),
            startDate: startDate.toISOString(),
        }).toString(),
        practitioner: sessionState.practitioner,
        practitionerRole: sessionState.practitionerRole,
    };
};

////////////////////////////////////////////////
/////  LEFT CTX DAY CAL. /////
///////////////////////////////////////////////

export const isManipulatingSlots = (manipState) =>
    !!manipState?.[SLOT_MANIPULATION_OPTIONS?.CREATE_SLOT] ||
    !!manipState?.[SLOT_MANIPULATION_OPTIONS?.LOCK_SLOT] ||
    !!manipState?.[SLOT_MANIPULATION_OPTIONS?.ASSIGN];

export const getManipulationKind = (manipState) => {
    if (!!manipState?.[SLOT_MANIPULATION_OPTIONS?.CREATE_SLOT])
        return SLOT_MANIPULATION_OPTIONS.CREATE_SLOT;
    if (!!manipState?.[SLOT_MANIPULATION_OPTIONS?.LOCK_SLOT])
        return SLOT_MANIPULATION_OPTIONS.LOCK_SLOT;
    if (!!manipState?.[SLOT_MANIPULATION_OPTIONS?.ASSIGN])
        return SLOT_MANIPULATION_OPTIONS.ASSIGN;

    return null;
};

export const prepareSingleSessions = ({state, reason, lockedSessions}) => {
    const clone = _.cloneDeep(state);
    const preparedEvents = [];

    for (let sessionId in clone) {
        const tempAssignedSingleEvents = clone[sessionId];

        for (let ev of tempAssignedSingleEvents) {
            const preparedEv = {
                active: true,
                singleEvent: true,
                sessionId: uuidv4(),
                endDate: ev?.endDate,
                fixedSlotDuration: true,
                locked: !!lockedSessions,
                startDate: ev?.startDate,
                reason: ev?.lockedReason || '',
                sessionName: ev.sessionName || '',
                slotDuration: getSessionSlotDuration(ev),
            };

            if (ev?.isBars) {
                preparedEv.isBars = true;
            }

            if (!!lockedSessions && sessionId !== 'outsideSession') {
                preparedEv.parentSession = sessionId;
            }

            preparedEvents.push(preparedEv);
        }
    }

    return preparedEvents;
};
